import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Business,
  Inventory,
  AttachMoney,
  TrendingUp,
  Search,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { itemSuppliersService, ItemSupplier } from '../../../services/itemSuppliers.service';
import { useConfirm } from '../../../hooks/useConfirm';
import ItemSupplierDialog from '../../../components/dialogs/ItemSupplierDialog';

const translations = {
  ar: {
    title: 'إدارة تعيين الموردين للأصناف',
    subtitle: 'عرض وإدارة الموردين المعينين لكل صنف',
    addNew: 'إضافة تعيين جديد',
    search: 'بحث...',
    item: 'الصنف',
    supplier: 'المورد',
    unit: 'الوحدة',
    price: 'السعر',
    preferred: 'مفضل',
    active: 'نشط',
    actions: 'الإجراءات',
    edit: 'تعديل',
    delete: 'حذف',
    deleteConfirm: 'هل أنت متأكد من حذف هذا التعيين؟',
    deleteSuccess: 'تم الحذف بنجاح',
    deleteError: 'فشل الحذف',
    loadError: 'فشل تحميل البيانات',
    noData: 'لا توجد تعيينات',
    lastPurchase: 'آخر شراء',
    yes: 'نعم',
    no: 'لا',
  },
  en: {
    title: 'Item-Supplier Assignment Management',
    subtitle: 'View and manage suppliers assigned to each item',
    addNew: 'Add New Assignment',
    search: 'Search...',
    item: 'Item',
    supplier: 'Supplier',
    unit: 'Unit',
    price: 'Price',
    preferred: 'Preferred',
    active: 'Active',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    deleteConfirm: 'Are you sure you want to delete this assignment?',
    deleteSuccess: 'Deleted successfully',
    deleteError: 'Failed to delete',
    loadError: 'Failed to load data',
    noData: 'No assignments found',
    lastPurchase: 'Last Purchase',
    yes: 'Yes',
    no: 'No',
  },
};

export default function ItemSupplierManagement() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<ItemSupplier[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<ItemSupplier[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<ItemSupplier | null>(null);

  useEffect(() => {
    loadAssignments();
  }, []);

  useEffect(() => {
    filterAssignments();
  }, [searchTerm, assignments]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const data = await itemSuppliersService.getAll();
      setAssignments(data);
    } catch (error) {
      console.error('Failed to load assignments:', error);
      toast.error(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const filterAssignments = () => {
    if (!searchTerm.trim()) {
      setFilteredAssignments(assignments);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = assignments.filter(
      (a) =>
        a.itemName?.toLowerCase().includes(term) ||
        a.itemNameAr?.toLowerCase().includes(term) ||
        a.itemCode?.toLowerCase().includes(term) ||
        a.supplierName?.toLowerCase().includes(term) ||
        a.supplierNameAr?.toLowerCase().includes(term) ||
        a.supplierCode?.toLowerCase().includes(term)
    );
    setFilteredAssignments(filtered);
  };

  const handleAddNew = () => {
    setSelectedAssignment(null);
    setDialogOpen(true);
  };

  const handleEdit = (assignment: ItemSupplier) => {
    setSelectedAssignment(assignment);
    setDialogOpen(true);
  };

  const handleDelete = async (assignment: ItemSupplier) => {
    if (!(await confirm({ message: t.deleteConfirm, severity: 'error' }))) return;

    try {
      await itemSuppliersService.delete(assignment.itemSupplierId);
      toast.success(t.deleteSuccess);
      loadAssignments();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(t.deleteError);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAssignment(null);
  };

  const handleSaved = () => {
    loadAssignments();
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        mt: 3,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
          <Box
            sx={{
              bgcolor: 'rgba(250, 112, 154, 0.1)',
              p: 1.5,
              borderRadius: 2,
            }}
          >
            <Business sx={{ color: '#fa709a', fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography variant="h6" fontWeight="600">
              {t.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t.subtitle}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            sx={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              fontWeight: 600,
              px: 3,
            }}
          >
            {t.addNew}
          </Button>
        </Stack>

        {/* Search */}
        <TextField
          fullWidth
          placeholder={t.search}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {/* Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : filteredAssignments.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {t.noData}
          </Alert>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableRow>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Inventory fontSize="small" />
                      <Typography variant="subtitle2" fontWeight="600">
                        {t.item}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Business fontSize="small" />
                      <Typography variant="subtitle2" fontWeight="600">
                        {t.supplier}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{t.unit}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AttachMoney fontSize="small" />
                      <Typography variant="subtitle2" fontWeight="600">
                        {t.price}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">{t.preferred}</TableCell>
                  <TableCell align="center">{t.active}</TableCell>
                  <TableCell align="center">{t.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAssignments.map((assignment) => (
                  <TableRow
                    key={assignment.itemSupplierId}
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: 'rgba(250, 112, 154, 0.04)',
                      },
                    }}
                  >
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" fontWeight="600">
                          {assignment.itemCode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isRTL ? assignment.itemNameAr : assignment.itemName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" fontWeight="600">
                          {assignment.supplierCode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isRTL ? assignment.supplierNameAr : assignment.supplierName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {isRTL ? assignment.unitNameAr : assignment.unitName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" fontWeight="600" color="success.main">
                          {assignment.supplierPrice.toFixed(3)} KD
                        </Typography>
                        {assignment.lastPurchaseDate && (
                          <Typography variant="caption" color="text.secondary">
                            {t.lastPurchase}: {assignment.lastPurchasePrice?.toFixed(3)} KD
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {assignment.isPreferred ? (
                        <Chip
                          label={t.yes}
                          size="small"
                          color="success"
                          icon={<TrendingUp />}
                        />
                      ) : (
                        <Chip label={t.no} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {assignment.isActive ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Cancel color="disabled" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title={t.edit}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(assignment)}
                            sx={{ color: '#667eea' }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.delete}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(assignment)}
                            sx={{ color: '#ef4444' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Dialog */}
      <ItemSupplierDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        itemSupplier={selectedAssignment}
        onSaved={handleSaved}
      />
    </Card>
  );
}